import type {
  CsvValidationResponse,
  ImportColumnMappingTargetKind,
  ImportEntityType,
} from "@adva/types";

type WizardStep = 1 | 2 | 3 | 4;

/**
 * A user's explicit decision about where to route a CSV header. Keyed by
 * source_header in `mappingOverrides`. Seeded from the validation response
 * on the first render of step 2; mutated by the column mapper UI.
 */
export interface ColumnMappingOverride {
  source_header: string;
  target_kind: ImportColumnMappingTargetKind;
  target_field: string | null;
}

// ADV-1055: the wizard formerly tracked an "Import mode" selection
// (incremental | full). `full` was an unfinished destructive feature and was
// removed from the API; the wizard no longer offers a choice and the API
// defaults the request body to incremental.
export interface WizardState {
  step: WizardStep;
  entityType: ImportEntityType | null;
  file: File | null;
  validation: CsvValidationResponse | null;
  // ADV-973: counted client-side from the full file in step 1. The validation
  // payload reflects the sampled file (first SAMPLE_ROW_COUNT rows), so the
  // confirm step needs this number to show the true "Rows in file" total.
  totalRows: number | null;
  mappingOverrides: Record<string, ColumnMappingOverride>;
  error: string | null;
}

type WizardAction =
  | { type: "SELECT_ENTITY"; entityType: ImportEntityType }
  | { type: "SELECT_FILE"; file: File }
  | { type: "CLEAR_FILE" }
  | {
      type: "SET_VALIDATION";
      validation: CsvValidationResponse;
      totalRows?: number;
    }
  | { type: "SET_MAPPING"; override: ColumnMappingOverride }
  | { type: "CLEAR_MAPPING"; source_header: string }
  | { type: "SET_ERROR"; error: string | null }
  | { type: "GO_TO_STEP"; step: WizardStep }
  | { type: "RESET" };

export const initialState: WizardState = {
  step: 1,
  entityType: null,
  file: null,
  validation: null,
  totalRows: null,
  mappingOverrides: {},
  error: null,
};

export function wizardReducer(
  state: WizardState,
  action: WizardAction,
): WizardState {
  switch (action.type) {
    case "SELECT_ENTITY":
      // Changing entity invalidates downstream state.
      return {
        ...state,
        entityType: action.entityType,
        file: null,
        validation: null,
        totalRows: null,
        mappingOverrides: {},
        error: null,
      };
    case "SELECT_FILE":
      return {
        ...state,
        file: action.file,
        validation: null,
        totalRows: null,
        mappingOverrides: {},
        error: null,
      };
    case "CLEAR_FILE":
      return {
        ...state,
        file: null,
        validation: null,
        totalRows: null,
        mappingOverrides: {},
        error: null,
      };
    case "SET_VALIDATION":
      return {
        ...state,
        validation: action.validation,
        totalRows: action.totalRows ?? state.totalRows,
        step: 2,
        error: null,
      };
    case "SET_MAPPING":
      return {
        ...state,
        mappingOverrides: {
          ...state.mappingOverrides,
          [action.override.source_header]: action.override,
        },
      };
    case "CLEAR_MAPPING": {
      const next = { ...state.mappingOverrides };
      delete next[action.source_header];
      return { ...state, mappingOverrides: next };
    }
    case "SET_ERROR":
      return { ...state, error: action.error };
    case "GO_TO_STEP":
      return { ...state, step: action.step, error: null };
    case "RESET":
      return initialState;
    default:
      return state;
  }
}
